/**
 * Coaching API client — Stage 9 read-only context and Stage 10
 * deterministic explanations. Reuses the shared authenticated ``http``
 * axios instance, adds abort-signal plumbing, and normalises errors so
 * pages can render safe messages without exposing backend traces.
 */
import { http } from "@/lib/api";

const STAGE9_ROOT = "/coaching-context";
const STAGE10_ROOT = "/coaching-explanations";

const normaliseError = (err) => {
  if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
    return { code: "aborted", message: "Request cancelled" };
  }
  if (err?.code === "ERR_BACKEND_URL_MISSING") {
    return {
      code: "config",
      message:
        "Coach is not configured. Set REACT_APP_BACKEND_URL and reload.",
    };
  }
  if (err?.message === "Network Error" || err?.code === "ERR_NETWORK") {
    return {
      code: "network",
      message: "Cannot connect to the Endurava server.",
    };
  }
  const status = err?.response?.status;
  if (status === 401)
    return { code: "unauthenticated", status,
              message: "Your session has expired. Please sign in again." };
  if (status === 403)
    return { code: "forbidden", status,
              message: "You don't have access to this coaching resource." };
  if (status === 404)
    return { code: "not_found", status,
              message: "This coaching resource is not available." };
  if (status === 409)
    return { code: "conflict", status,
              message: "Coaching data conflict — please refresh." };
  if (status === 422)
    return { code: "invalid_request", status,
              message: "Invalid request — please refresh and try again." };
  if (status === 429)
    return { code: "rate_limited", status,
              message: "Coach is busy. Please try again in a moment." };
  if (status >= 500)
    return { code: "server_error", status,
              message: "Coaching is temporarily unavailable." };
  return { code: "unknown", status,
            message: "Coaching is temporarily unavailable." };
};

const request = async (path, { params, signal } = {}) => {
  try {
    const res = await http.get(path, { params, signal });
    return res.data;
  } catch (err) {
    const wrapped = new Error(normaliseError(err).message);
    wrapped.coachingError = normaliseError(err);
    wrapped.status = err?.response?.status;
    throw wrapped;
  }
};

// ---------- Stage 9 context --------------------------------------------

export const getCoachingContext = (params, opts) =>
  request(STAGE9_ROOT, { params, ...opts });

export const getTodayContext = (params, opts) =>
  request(`${STAGE9_ROOT}/today`, { params, ...opts });

export const getWeekContext = (params, opts) =>
  request(`${STAGE9_ROOT}/week`, { params, ...opts });

export const getBlockContext = (params, opts) =>
  request(`${STAGE9_ROOT}/block`, { params, ...opts });

export const getRecentTraining = (params, opts) =>
  request(`${STAGE9_ROOT}/recent-training`, { params, ...opts });

export const getAdaptationContext = (params, opts) =>
  request(`${STAGE9_ROOT}/adaptation`, { params, ...opts });

export const getSafetyContext = (params, opts) =>
  request(`${STAGE9_ROOT}/safety`, { params, ...opts });

export const getCoachPendingAction = (params, opts) =>
  request(`${STAGE9_ROOT}/pending-action`, { params, ...opts });

// ---------- Stage 10 explanations --------------------------------------

const explanation = (path) => (params, opts) =>
  request(`${STAGE10_ROOT}${path}`, { params, ...opts });

export const getExplanation = (params, opts) =>
  request(STAGE10_ROOT, { params, ...opts });

export const getTodayExplanation = explanation("/today");
export const getTodayPurposeExplanation = explanation("/today/purpose");
export const getTodayFocusExplanation = explanation("/today/focus");
export const getLastSessionExplanation = explanation("/last-session");
export const getWeekComplianceExplanation = explanation("/compliance/week");
export const getBlockComplianceExplanation = explanation("/compliance/block");
export const getPlanChangeExplanation = explanation("/plan-change");
export const getSafetyExplanation = explanation("/safety");
export const getMissingInformationExplanation =
  explanation("/missing-information");

export const _internal = { normaliseError };
